"use client";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../";
import { Badge } from "../../";
import type { Tool } from "ai";
import { IconRobot } from "@tabler/icons-react";
import type { ComponentProps } from "react";
import { memo } from "react";
import { AiCodeBlock as CodeBlock } from "../code-block/code-block";
import { cn } from "../../utils";

export type AgentProps = ComponentProps<"div">;

export const Agent = memo(({ className, ...props }: AgentProps) => (
  <div className={cn("al-not-prose al-w-full al-rounded-md al-border", className)} {...props} />
));

export type AgentHeaderProps = ComponentProps<"div"> & {
  name: string;
  model?: string;
};

export const AgentHeader = memo(({ className, name, model, ...props }: AgentHeaderProps) => (
  <div
    className={cn(
      "al-flex al-w-full al-items-center al-justify-between al-gap-4 al-p-3",
      className
    )}
    {...props}
  >
    <div className="al-flex al-items-center al-gap-2">
      <IconRobot className="al-size-4 al-text-muted-foreground" />
      <span className="al-font-medium al-text-sm">{name}</span>
      {model && (
        <Badge className="al-font-mono al-text-xs" variant="secondary">
          {model}
        </Badge>
      )}
    </div>
  </div>
));

export type AgentContentProps = ComponentProps<"div">;

export const AgentContent = memo(({ className, ...props }: AgentContentProps) => (
  <div className={cn("al-space-y-4 al-p-4 al-pt-0", className)} {...props} />
));

export type AgentInstructionsProps = ComponentProps<"div"> & {
  children: string;
};

export const AgentInstructions = memo(
  ({ className, children, ...props }: AgentInstructionsProps) => (
    <div className={cn("al-space-y-2", className)} {...props}>
      <span className="al-font-medium al-text-muted-foreground al-text-sm">Instructions</span>
      <div className="al-rounded-md al-bg-muted/50 al-p-3 al-text-muted-foreground al-text-sm">
        <p>{children}</p>
      </div>
    </div>
  )
);

export type AgentToolsProps = ComponentProps<typeof Accordion>;

export const AgentTools = memo(({ className, ...props }: AgentToolsProps) => (
  <div className={cn("al-space-y-2", className)}>
    <span className="al-font-medium al-text-muted-foreground al-text-sm">Tools</span>
    <Accordion className="al-rounded-md al-border" {...props} />
  </div>
));

export type AgentToolProps = ComponentProps<typeof AccordionItem> & {
  tool: Tool;
};

export const AgentTool = memo(({ className, tool, value, ...props }: AgentToolProps) => {
  const schema = "jsonSchema" in tool && tool.jsonSchema ? tool.jsonSchema : tool.inputSchema;

  return (
    <AccordionItem
      className={cn("al-border-b last:al-border-b-0", className)}
      value={value}
      {...props}
    >
      <AccordionTrigger className="al-px-3 al-py-2 al-text-sm hover:al-no-underline">
        {tool.description ?? "No description"}
      </AccordionTrigger>
      <AccordionContent className="al-px-3 al-pb-3">
        <div className="al-rounded-md al-bg-muted/50">
          <CodeBlock code={JSON.stringify(schema, null, 2)} language="json" />
        </div>
      </AccordionContent>
    </AccordionItem>
  );
});

export type AgentOutputProps = ComponentProps<"div"> & {
  schema: string;
};

export const AgentOutput = memo(({ className, schema, ...props }: AgentOutputProps) => (
  <div className={cn("al-space-y-2", className)} {...props}>
    <span className="al-font-medium al-text-muted-foreground al-text-sm">Output Schema</span>
    <div className="al-rounded-md al-bg-muted/50">
      <CodeBlock code={schema} language="typescript" />
    </div>
  </div>
));

Agent.displayName = "Agent";
AgentHeader.displayName = "AgentHeader";
AgentContent.displayName = "AgentContent";
AgentInstructions.displayName = "AgentInstructions";
AgentTools.displayName = "AgentTools";
AgentTool.displayName = "AgentTool";
AgentOutput.displayName = "AgentOutput";
